import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ErrorStore,
  FormHeaderComponent,
  GlobalErrorComponent,
  IdentityStore,
  SetPassword,
  SetPasswordFormComponent,
  TenantStore,
} from '../../../index';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FaIconComponent, MessageComponent } from '@akostm/orbit-ui';

@Component({
  selector: 'shared-main-set-password',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    MessageComponent,
    TranslatePipe,
    SetPasswordFormComponent,
    FormHeaderComponent,
    GlobalErrorComponent,
  ],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetPasswordComponent implements OnInit {
  readonly tenantStore = inject(TenantStore);
  readonly #identityStore = inject(IdentityStore);
  readonly spinnerIcon = faSpinnerThird;
  readonly #translateService: TranslateService = inject(TranslateService);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router: Router = inject(Router);
  readonly #toastr = inject(ToastrService);
  readonly #errorStore = inject(ErrorStore);
  readonly #tenantErrorCodes = [
    'error.identity.tenant_not_found_or_disabled',
    'error.frontends.tenant_not_found_or_disabled',
  ];
  readonly tenantErrors = computed(() =>
    this.#errorStore.getErrorsByCodesAndType(this.#tenantErrorCodes, 'global')
  );

  error: WritableSignal<string | null> = signal(this.tenantStore.error());
  setNewPasswordToken!: string;
  email!: string;

  ngOnInit() {
    this.#activatedRoute.queryParams.subscribe((params) => {
      const token = params['token'];
      const email = params['email'];
      if (!token || !email) {
        const key = !token
          ? 'SHARED.PAGES.SET_PASSWORD.MISSING_TOKEN'
          : 'SHARED.PAGES.SET_PASSWORD.MISSING_EMAIL';
        this.#toastr.error(this.#translateService.instant(key));
        this.#router.navigate(['/login']);
      } else {
        this.setNewPasswordToken = token;
        this.email = email;
      }
    });
  }

  setPassword(request: SetPassword) {
    this.#identityStore
      .setPassword({
        email: this.email,
        password: request.password,
        token: this.setNewPasswordToken,
      })
      .then(() => {
        this.#toastr.success(
          this.#translateService.instant('SHARED.PAGES.SET_PASSWORD.PASSWORD_CHANGED_SUCCESSFULLY')
        );
        this.#router.navigate(['/login']);
      })
      .catch(() => {
        this.error.set(
          this.#translateService.instant('SHARED.PAGES.SET_PASSWORD.COULD_NOT_CHANGE_PASSWORD')
        );
      });
  }
}
